import {
  roleRank,
  SEAT_ROLES,
  type Level,
  type Role,
  type SeatRole,
  type Verdict,
} from './constants';
import { parseAuthoredFields, type AuthoredField } from './definitions';
import {
  FACT_RULE_WORD,
  factRuleCondition,
  parseFlowFactRule,
  ruleIdentity,
  type FlowFactRule,
} from './fact-rules';
import { CONDITION_FACT_KINDS } from './facts';
import { flowModuleKey } from './flows';
import type { ScalarFieldValue } from './module-kit';
import {
  evaluatePredicate,
  holdsSelf,
  matchesStateCriterion,
  normalizeEmptyScalar,
  parseCriteriaPredicate,
  isNegated,
  JUDGMENTS,
  type CriteriaPredicate,
  type Judgment,
  type StateCriterion,
  type VectorSnapshot,
} from './search-criteria';
import {
  describeMeans,
  describeMeansPair,
  joinPairs,
  keyWord,
  meansPairIdentity,
  parseFlowPhaseRow,
  stageError,
  type FlowPhaseRow,
  type MeansPair,
} from './stage-gates';
import { isQuestionClass, parsePolicy, reachableQuestionClasses } from './resolution/policies';
import type { Policy, QuestionClass } from './resolution/types';
import { m, verbatim, type Message } from '@/i18n';
import type { Command, DomainError } from './types';
import { errOf, isRecord, ok, type Result } from './types';

/**
 * The flow definition as a SELECT (docs/FLOW_DEFINITION.md §1/§4): one
 * dimension, n options, each option a **word** (the stage label) plus optional
 * **derive-predicate** (membership computed from the vector - a DERIVED stage)
 * and optional **Who / When / Then** wiring. Without a derive-predicate the
 * option is a STORED stage: membership is the logged position (the location
 * tier, docs/FLOW.md). This file owns the option/definition shapes, their
 * strict parse, the whole-definition validation (referenced catalog keys and
 * fields exist, §3 law 3's pairing flag), and the retirement surface (§6:
 * removals return their references, never silently accepted).
 *
 * EXTENDS the built flow machinery, never forks it: an option IS a
 * `FlowPhaseRow` (stage-gates.ts - key, label, `means`, the v0 gate) plus
 * the new wiring blocks, and a `FlowDefinition` is a strict superset of
 * `FlowPayload` (definitions.ts - same `stages`/`fields` properties), so a
 * stored payload without wiring parses unchanged. Versioning stores nothing
 * here: version = the definition's event seq (§6 - the log IS the snapshot
 * store; provenance stamps index into it).
 */

// --- Who: a selector over EXISTING authority facts (§1) ----------------------

/** The two group roles a Who may select (`viewer` deliberately excluded: sight
 *  is never move authority). */
export type GroupRole = Extract<Role, 'member' | 'owner'>;
export const GROUP_ROLES = ['member', 'owner'] as const satisfies readonly GroupRole[];

/**
 * The Who block (§1): DERIVED from access and seats, never an additional
 * layer. It selects over authority facts that already exist - a seat of the
 * item, a role in the flow's owning group, or plain write standing - and
 * stores nothing of its own (no per-flow ACLs, nothing new to audit).
 */
export type WhoSelector =
  | { kind: 'anyone-with-write' }
  | { kind: 'seat'; role: SeatRole }
  | { kind: 'group-role'; role: GroupRole };

/** The platform posture (§1): no Who means anyone with write may move, and
 *  out-of-role moves flag rather than block. Naming a Who is what upgrades
 *  flag to refusal - strictness is authored, never inherited. */
export const DEFAULT_WHO: WhoSelector = { kind: 'anyone-with-write' };

export function parseWhoSelector(value: unknown): Result<WhoSelector> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.who.notObject'));
  switch (value.kind) {
    case 'anyone-with-write':
      return ok({ kind: 'anyone-with-write' });
    case 'seat':
      if (!(SEAT_ROLES as readonly unknown[]).includes(value.role)) {
        return errOf(
          'invalid_input',
          m('flowErrors.who.seatRole', { roles: SEAT_ROLES.join(' | ') }),
        );
      }
      return ok({ kind: 'seat', role: value.role as SeatRole });
    case 'group-role':
      if (!(GROUP_ROLES as readonly unknown[]).includes(value.role)) {
        return errOf(
          'invalid_input',
          m('flowErrors.who.groupRole', { roles: GROUP_ROLES.join(' | ') }),
        );
      }
      return ok({ kind: 'group-role', role: value.role as GroupRole });
    default:
      return errOf('invalid_input', m('flowErrors.who.unknownKind'));
  }
}

// --- Then: the closed action vocabulary (§1, rulings §7.1) --------------------

/**
 * The canonical commands an action may dispatch: the lifecycle acts
 * (Tender/Retract/Close/Reopen/SetActive - the three-atom act inventory,
 * docs/CLOSED_AXIS.md), plus the verdict acts (Accept since ruling §7.6;
 * Dispute and Resolve since the §11 canon, 2026-07-22 - a `means` verdict
 * pair dispatches whichever act reaches its value, graded by `decide()`'s
 * own who-did-it law like every act). It never restructures the action row
 * (ruling §7.1: v1 rows are full-engine-shaped).
 */
export type DispatchableCommand = Extract<
  Command,
  | { type: 'Accept' }
  | { type: 'Dispute' }
  | { type: 'Resolve' }
  | { type: 'Tender' }
  | { type: 'Retract' }
  | { type: 'Close' }
  | { type: 'Reopen' }
  | { type: 'SetActive' }
>;

/**
 * The Then vocabulary is CLOSED (§1): dispatch a canonical command, declare or
 * clear a catalog health state by key (executed with flow provenance - §3 law
 * 1, the applied state is an ordinary claim in the collector), or set a main
 * or flow-scoped field value. No new permission concepts, no free-form acts.
 * A declare carries no grade: grades are fixed channels owned by the catalog.
 */
export type FlowAction =
  | {
      kind: 'dispatch';
      command: DispatchableCommand;
      /**
       * How the rule answers each question its act can raise (docs/RESOLUTION.md
       * §3, §12 step 6): an automation never waits, so a question it hits is
       * answered by the policy written here - a pre-named answer, `propose`
       * (filed once with the item's owners), or `skip` (nothing done, and a
       * finding says so). `validateFlowDefinition` fills every reachable class
       * the author left open with `skip`, so the saved rule says it visibly.
       */
      policies?: DispatchPolicies;
    }
  | { kind: 'declare'; tagKey: string }
  | { kind: 'clear'; tagKey: string }
  | { kind: 'set-field'; moduleKey: string; fieldKey: string; value: ScalarFieldValue };

/** A dispatch action's policies, one per question class it can reach. */
export type DispatchPolicies = Partial<Record<QuestionClass, Policy>>;

/** The questions a dispatch action's act can raise (§3). Pure. */
export function dispatchQuestionClasses(command: DispatchableCommand): QuestionClass[] {
  return reachableQuestionClasses({ kind: 'act', target: { command } });
}

/** Parse the optional `policies` of a dispatch row: known classes only, and a
 *  policy each class can take. Pure. */
function parseDispatchPolicies(value: unknown): Result<DispatchPolicies | undefined> {
  if (value === undefined || value === null) return ok(undefined);
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.dispatch.policiesNotObject'));
  const out: DispatchPolicies = {};
  for (const [klass, raw] of Object.entries(value)) {
    if (!isQuestionClass(klass)) {
      return errOf('invalid_input', m('flowErrors.dispatch.unknownQuestion', { question: klass }));
    }
    const parsed = parsePolicy(klass, raw);
    if (!parsed.ok) return errOf('invalid_input', parsed.message);
    out[klass] = parsed.policy;
  }
  return ok(out);
}

/**
 * The copy-safe self reference: inside a flow definition, the flow's OWN field
 * namespace is written as `flow:self`, never as the definition's concrete
 * `flow:<id>` key - a definition is born as a copy of a working flow (§5) and
 * an embedded concrete id would go stale on copy. Dispatch-time callers
 * resolve it via `resolveFlowSelfKeys`.
 */
export const FLOW_SELF_MODULE_KEY: string = flowModuleKey('self');

/** The canonical command a dispatch row runs (§1's closed vocabulary). Pure. */
function parseDispatchCommand(command: unknown): Result<DispatchableCommand> {
  if (!isRecord(command)) {
    return errOf('invalid_input', m('flowErrors.dispatch.notCommand'));
  }
  switch (command.type) {
    // The lifecycle ACTS + the activity declaration (the three-atom act
    // inventory, docs/CLOSED_AXIS.md): the runtime parse arms so a flow
    // Then-block can actually dispatch them.
    case 'Tender':
    case 'Retract':
    case 'Reopen':
      // Parameterless standing moves - the act IS the value.
      return ok({ type: command.type });
    case 'Close': {
      // The conclusion; the reason is OPTIONAL and rides the event
      // (empty/whitespace = absent).
      const reason = typeof command.reason === 'string' ? command.reason.trim() : '';
      return ok(reason.length > 0 ? { type: 'Close', reason } : { type: 'Close' });
    }
    case 'SetActive': {
      if (typeof command.active !== 'boolean') {
        return errOf('invalid_input', m('flowErrors.dispatch.setActiveBoolean'));
      }
      return ok({ type: 'SetActive', active: command.active });
    }
    case 'Accept':
    case 'Dispute':
    case 'Resolve':
      // The verdict acts (ruling §7.6 + the §11 canon): the act IS the
      // value, so the row carries no parameters (a note/reason is a
      // human's caveat, never wired).
      return ok({ type: command.type });
    default:
      return errOf('invalid_input', m('flowErrors.dispatch.unknownCommand'));
  }
}

export function parseFlowAction(value: unknown): Result<FlowAction> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.action.notObject'));
  switch (value.kind) {
    case 'dispatch': {
      const parsed = parseDispatchCommand(value.command);
      if (!parsed.ok) return parsed;
      const policies = parseDispatchPolicies(value.policies);
      if (!policies.ok) return policies;
      return ok({
        kind: 'dispatch',
        command: parsed.value,
        ...(policies.value ? { policies: policies.value } : {}),
      });
    }
    case 'declare':
    case 'clear': {
      const tagKey = typeof value.tagKey === 'string' ? value.tagKey.trim() : '';
      if (tagKey.length === 0) {
        return errOf('invalid_input', m('flowErrors.action.needsTagKey', { kind: value.kind }));
      }
      return ok(value.kind === 'declare' ? { kind: 'declare', tagKey } : { kind: 'clear', tagKey });
    }
    case 'set-field': {
      const moduleKey = typeof value.moduleKey === 'string' ? value.moduleKey.trim() : '';
      const fieldKey = typeof value.fieldKey === 'string' ? value.fieldKey.trim() : '';
      if (moduleKey.length === 0 || fieldKey.length === 0) {
        return errOf('invalid_input', m('flowErrors.action.setFieldKeys'));
      }
      const v = value.value === undefined ? null : value.value;
      if (v !== null && typeof v !== 'string' && typeof v !== 'number') {
        return errOf('invalid_input', m('flowErrors.action.setFieldScalar'));
      }
      return ok({ kind: 'set-field', moduleKey, fieldKey, value: v });
    }
    default:
      return errOf('invalid_input', m('flowErrors.action.unknownKind'));
  }
}

function parseFlowActions(value: unknown, block: string): Result<FlowAction[]> {
  if (!Array.isArray(value))
    return errOf('invalid_input', m('flowErrors.action.notList', { block }));
  const out: FlowAction[] = [];
  for (const raw of value) {
    const action = parseFlowAction(raw);
    if (!action.ok) return action;
    out.push(action.value);
  }
  return ok(out);
}

// --- The option: word + derive-predicate? + Who/When/Then wiring (§1, §4) -----

export type FlowOptionKind = 'derived' | 'stored';

export type FlowOptionWiring = {
  /** who may move items in/out; absent = `DEFAULT_WHO` (anyone-with-write,
   *  out-of-role flags rather than blocks). */
  who?: WhoSelector;
  /** the entry gate: what must be true of the vector to enter. */
  whenIn?: CriteriaPredicate;
  /** the exit gate: what must be true of the vector to leave. */
  whenOut?: CriteriaPredicate;
  /** what entering applies (fired on the human gesture, §4 loop guard). */
  thenIn?: FlowAction[];
  /** what leaving applies. */
  thenOut?: FlowAction[];
};

/**
 * One option of the select. `label` is the Word (§1). The kind is explicit in
 * the type: `derived` carries a derive-predicate - membership is COMPUTED from
 * the vector, nothing stored, the card can never disagree with the machine;
 * `stored` has none - membership is the logged position. The v0 `gate` and
 * the `means` pairs ride along from `FlowPhaseRow` (extend, don't fork).
 *
 * A STORED option's non-empty `means` is also its PULL condition (§11): an
 * act that takes the entire set from not-holding to holding moves the member
 * here - an ordinary position write (attributed `automation:flow`,
 * provenanced, edge-triggered), never a computed display. The separate
 * authored `target` predicate of §10.1 is RETIRED: the pairs ARE the target.
 */
export type FlowOption = FlowPhaseRow &
  FlowOptionWiring &
  (
    | { kind: 'derived'; derivePredicate: CriteriaPredicate }
    | { kind: 'stored'; derivePredicate?: undefined }
  );

/**
 * Parse one predicate of a flow definition (a When gate, a derive-predicate):
 * the grammar's strict parse (`parseCriteriaPredicate`), then the one value a
 * flow refuses. `self` (`SELF_VALUE`, docs/SEARCH.md "Parties") stands for the
 * person who opens a filter, and a flow evaluates an item for no one in
 * particular (the automation, a gate, a derived stage), so it would name
 * nobody. The rule conditions are built from these two blocks, so this is the
 * one place a flow takes a predicate in.
 */
export function parseFlowPredicate(value: unknown): Result<CriteriaPredicate> {
  const pred = parseCriteriaPredicate(value);
  if (!pred.ok) return pred;
  if (pred.value.criteria.some(holdsSelf)) {
    return errOf('invalid_input', m('flowErrors.predicate.self'));
  }
  return pred;
}

/** Parse one option: the base stage row (via the one existing row parser) plus
 *  the wiring blocks, kind minted from the derive-predicate's presence. */
export function parseFlowOption(value: unknown): Result<FlowOption> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.stage.notObject'));
  const base = parseFlowPhaseRow(value);
  if (!base.ok) return base;
  const key = base.value.key;
  // Re-wrap block-parse failures with the stage named (editor legibility).
  const at = (r: { ok: false; error: DomainError }) =>
    errOf(r.error.code, stageError(key, r.error));

  const wiring: FlowOptionWiring = {};
  if (value.who !== undefined && value.who !== null) {
    const who = parseWhoSelector(value.who);
    if (!who.ok) return at(who);
    wiring.who = who.value;
  }
  for (const block of ['whenIn', 'whenOut'] as const) {
    const raw = value[block];
    if (raw === undefined || raw === null) continue;
    const pred = parseFlowPredicate(raw);
    if (!pred.ok) return at(pred);
    wiring[block] = pred.value;
  }
  for (const block of ['thenIn', 'thenOut'] as const) {
    const raw = value[block];
    if (raw === undefined || raw === null) continue;
    const actions = parseFlowActions(raw, block);
    if (!actions.ok) return at(actions);
    wiring[block] = actions.value;
  }

  // The §10.1 authored `target` predicate is RETIRED (§11: the means pairs ARE
  // the pull condition). Refused loudly at the authoring path; stored payloads
  // were converted by the migration.
  if (value.target !== undefined && value.target !== null) {
    return at(errOf('invalid_input', m('flowErrors.stage.targetRetired')));
  }
  if (value.derivePredicate !== undefined && value.derivePredicate !== null) {
    const pred = parseFlowPredicate(value.derivePredicate);
    if (!pred.ok) return at(pred);
    return ok({ ...base.value, ...wiring, kind: 'derived', derivePredicate: pred.value });
  }
  return ok({ ...base.value, ...wiring, kind: 'stored' });
}

// --- The whole definition -----------------------------------------------------

/** A strict superset of `FlowPayload` (definitions.ts): same `name`/`stages`/
 *  `fields` properties, options widened with the wiring. `fields` reuses the
 *  authored-field shape (the flow-scoped vocabulary, owned by the flow). */
export type FlowDefinition = {
  name: string;
  stages: FlowOption[];
  fields: AuthoredField[];
  /** The flow's FACT RULES (docs/THRESHOLDS.md §2): "at 14 days of quiet, call
   *  it Stalled, at warning". The threshold and the word are judgments, so they
   *  need an author, and a flow is the only jurisdiction an item consented to.
   *  Empty on every flow that has not authored any - an item in no flow, or in
   *  a flow with no rules, has facts and no grades. */
  factRules: FlowFactRule[];
};

/** Shape-parse a whole definition: every option, unique stable option keys,
 *  the flow-scoped fields (unique keys via `parseAuthoredFields`). Semantic
 *  checks (references exist, pairing) live in `validateFlowDefinition`. */
export function parseFlowDefinition(value: unknown): Result<FlowDefinition> {
  if (!isRecord(value)) return errOf('invalid_input', m('flowErrors.definition.notObject'));
  if (value.name !== undefined && typeof value.name !== 'string') {
    return errOf('invalid_input', m('flowErrors.definition.nameNotString'));
  }
  const name = typeof value.name === 'string' ? value.name.trim() : '';
  if (!Array.isArray(value.stages)) return errOf('invalid_input', m('flowErrors.stages.notList'));
  const stages: FlowOption[] = [];
  const seen = new Set<string>();
  for (const raw of value.stages) {
    const option = parseFlowOption(raw);
    if (!option.ok) return option;
    if (seen.has(option.value.key)) {
      return errOf(
        'invalid_input',
        m('flowErrors.stages.duplicateKey', { stage: option.value.key }),
      );
    }
    seen.add(option.value.key);
    stages.push(option.value);
  }
  let fields: AuthoredField[] = [];
  if (value.fields !== undefined) {
    const parsedFields = parseAuthoredFields(value.fields);
    if (!parsedFields.ok) return parsedFields;
    fields = parsedFields.value;
  }
  const factRules: FlowFactRule[] = [];
  if (value.factRules !== undefined) {
    if (!Array.isArray(value.factRules)) {
      return errOf('invalid_input', m('flowErrors.factRules.notList'));
    }
    const seen = new Set<string>();
    for (const raw of value.factRules) {
      const rule = parseFlowFactRule(raw, m('flowErrors.where.factRule'));
      if (!rule.ok) return rule;
      // A stage condition names a STORED stage of this flow (docs/THRESHOLDS.md
      // §2, "Conditions"): a derived stage has no logged position to stand in.
      if (
        rule.value.fact === 'in_stage' &&
        !stages.some((s) => s.key === rule.value.stage && s.kind === 'stored')
      ) {
        return errOf(
          'invalid_input',
          m('flowErrors.factRules.unknownStage', { stage: rule.value.stage ?? '' }),
        );
      }
      // One word per statement per flow: two rules on the same fact (or the
      // same tag, the same stage) would put two words on one statement, and
      // the item would speak both.
      const identity = ruleIdentity(rule.value);
      if (seen.has(identity)) {
        return errOf(
          'invalid_input',
          CONDITION_FACT_KINDS.has(rule.value.fact)
            ? m('flowErrors.factRules.twoConditionRules', {
                condition: factRuleCondition(
                  rule.value,
                  stages.find((s) => s.key === rule.value.stage)?.label,
                ),
              })
            : m('flowErrors.factRules.twoRules', { fact: FACT_RULE_WORD[rule.value.fact] }),
        );
      }
      seen.add(identity);
      factRules.push(rule.value);
    }
  }
  return ok({ name, stages, fields, factRules });
}

// --- The means machinery: hold-check, apply plan, exclusivity (§11) -----------

/**
 * Does a stage's ENTIRE `means` set hold for one member's state (§11's pull
 * condition and the apply's no-op check)? An EMPTY set never holds: a manual
 * word applies nothing and never pulls - callers need no separate filter.
 * `selfModuleKey` is the flow's concrete `flow:<id>` namespace (a means field
 * pair names the flow's OWN field by bare key).
 */
export function meansHolds(
  means: readonly MeansPair[],
  snap: VectorSnapshot,
  selfModuleKey: string,
): boolean {
  if (means.length === 0) return false;
  return means.every((pair) => {
    switch (pair.attr) {
      case 'delivered':
        return snap.delivered === pair.value;
      case 'closed':
        return snap.closed === pair.value;
      case 'active':
        return snap.active === pair.value;
      case 'verdict':
        return (snap.verdict === 'open' ? 'none' : snap.verdict) === pair.value;
      case 'signal':
        return snap.tags.some((t) => t.key === pair.key) === (pair.value === 'worn');
      case 'field':
        return (
          normalizeEmptyScalar(snap.fieldValues[selfModuleKey]?.[pair.key] ?? null) ===
          normalizeEmptyScalar(pair.value)
        );
    }
  });
}

/**
 * Compile a stage's `means` into the ordered ACTS that apply it (§11: placing
 * an item in a phase applies exactly its pairs, each through the real act).
 * Pure; the caller dispatches through the existing mutators and tolerates a
 * benign `no_change`. Ordering is what makes every act legal against the
 * state its predecessors leave:
 *
 *   1. `closed: no`   -> Reopen (un-conclude before anything else moves);
 *   2. `delivered`    -> Tender / Retract (a verdict act needs the standing);
 *   3. `verdict`      -> Accept / Dispute / Resolve (none withdraws);
 *   4. `active`       -> SetActive;
 *   5. `closed: yes`  -> Close(reason) - LAST, so a cancelled-shaped word
 *      composes Retract-then-Close and the warning-unless-solo level law
 *      rides `decide()`'s own Close;
 *   6. tag pairs      -> declare / clear (the moving user's declaration);
 *   7. field pairs    -> set-field under the flow's own namespace.
 *
 * Atom/verdict acts are emitted only when the current state differs (the
 * minimal sequence); tag/field writes always ride (their mutators no-op
 * idempotently and need no gathered state here).
 */
export function meansApplyPlan(
  means: readonly MeansPair[],
  state: { delivered: boolean; active: boolean; closed: boolean; verdict: Verdict },
  selfModuleKey: string,
  closeReason?: string,
): FlowAction[] {
  const out: FlowAction[] = [];
  const atomPair = (attr: 'delivered' | 'closed' | 'active') =>
    means.find(
      (p): p is Extract<MeansPair, { value: boolean }> =>
        p.attr === attr && typeof p.value === 'boolean',
    );

  const closed = atomPair('closed');
  if (closed && !closed.value && state.closed) {
    out.push({ kind: 'dispatch', command: { type: 'Reopen' } });
  }
  const delivered = atomPair('delivered');
  if (delivered && delivered.value !== state.delivered) {
    out.push({ kind: 'dispatch', command: { type: delivered.value ? 'Tender' : 'Retract' } });
  }
  const verdict = means.find(
    (p): p is Extract<MeansPair, { attr: 'verdict' }> => p.attr === 'verdict',
  );
  if (verdict) {
    const current: Judgment = state.verdict === 'open' ? 'none' : state.verdict;
    if (verdict.value !== current) {
      out.push({
        kind: 'dispatch',
        command: {
          type:
            verdict.value === 'accepted'
              ? 'Accept'
              : verdict.value === 'disputed'
                ? 'Dispute'
                : 'Resolve',
        },
      });
    }
  }
  const active = atomPair('active');
  if (active && active.value !== state.active) {
    out.push({ kind: 'dispatch', command: { type: 'SetActive', active: active.value } });
  }
  if (closed && closed.value && !state.closed) {
    out.push({
      kind: 'dispatch',
      command:
        closeReason && closeReason.trim().length > 0
          ? { type: 'Close', reason: closeReason }
          : { type: 'Close' },
    });
  }
  for (const p of means) {
    if (p.attr === 'signal') {
      out.push({ kind: p.value === 'worn' ? 'declare' : 'clear', tagKey: p.key });
    } else if (p.attr === 'field') {
      out.push({ kind: 'set-field', moduleKey: selfModuleKey, fieldKey: p.key, value: p.value });
    }
  }
  return out;
}

// --- Save-time exclusivity (§11: pairwise exclusive over reachable states) ----

/** One colliding pair of words, with the example state (the refusal's
 *  material). `example` speaks the one pair vocabulary. */
export interface MeansCollision {
  aKey: string;
  aLabel: string;
  bKey: string;
  bLabel: string;
  /** a reachable attribute state under which BOTH sets fully hold. */
  example: Message;
}

/** The OTHER-value sentinel for field enumeration (a value different from
 *  every mentioned one - one abstract state, §11's reachability list). */
const FIELD_OTHER = Symbol('other');
type FieldEnumValue = string | number | typeof FIELD_OTHER;

interface MeansEnumState {
  delivered: boolean;
  closed: boolean;
  active: boolean;
  verdict: Judgment;
  tags: ReadonlyMap<string, boolean>;
  fields: ReadonlyMap<string, FieldEnumValue>;
}

function holdsInEnumState(means: readonly MeansPair[], s: MeansEnumState): boolean {
  return means.every((p) => {
    switch (p.attr) {
      case 'delivered':
        return s.delivered === p.value;
      case 'closed':
        return s.closed === p.value;
      case 'active':
        return s.active === p.value;
      case 'verdict':
        return s.verdict === p.value;
      case 'signal':
        return (s.tags.get(p.key) ?? false) === (p.value === 'worn');
      case 'field': {
        const v = s.fields.get(p.key);
        return (
          v !== FIELD_OTHER &&
          v !== undefined &&
          normalizeEmptyScalar(v) === normalizeEmptyScalar(p.value)
        );
      }
    }
  });
}

/**
 * Exhaustive enumeration of the reachable attribute states over the
 * dimensions the given sets mention (§11: never a heuristic - the space is
 * finite). Dimensions no set mentions cannot affect any set's holding, so
 * the enumeration restricts to the mentioned ones, under the reachability
 * constraints:
 *
 *   - `verdict != none` implies `delivered = yes` (judgment is scoped to a
 *     standing tender; Retract voids);
 *   - all 8 atom combinations otherwise reachable (`active` persists through
 *     close);
 *   - fields enumerate by mentioned values plus one abstract "other" state.
 *
 * Returns the first reachable state satisfying `test`, or null.
 */
function findReachableState(
  sets: readonly (readonly MeansPair[])[],
  test: (s: MeansEnumState) => boolean,
): MeansEnumState | null {
  const all = sets.flat();
  const tagKeys = [...new Set(all.filter((p) => p.attr === 'signal').map((p) => p.key))];
  const fieldPairs = all.filter(
    (p): p is Extract<MeansPair, { attr: 'field' }> => p.attr === 'field',
  );
  const fieldKeys = [...new Set(fieldPairs.map((p) => p.key))];
  const fieldValues = new Map<string, FieldEnumValue[]>(
    fieldKeys.map((key) => [
      key,
      [...new Set(fieldPairs.filter((p) => p.key === key).map((p) => p.value)), FIELD_OTHER],
    ]),
  );
  const verdicts: Judgment[] = all.some((p) => p.attr === 'verdict')
    ? ['none', 'accepted', 'disputed']
    : ['none'];

  const bools = [false, true];
  const tagAssignments = (i: number, acc: Map<string, boolean>): Map<string, boolean>[] => {
    if (i === tagKeys.length) return [new Map(acc)];
    return bools.flatMap((worn) => {
      acc.set(tagKeys[i]!, worn);
      const branch = tagAssignments(i + 1, acc);
      acc.delete(tagKeys[i]!);
      return branch;
    });
  };
  const fieldAssignments = (
    i: number,
    acc: Map<string, FieldEnumValue>,
  ): Map<string, FieldEnumValue>[] => {
    if (i === fieldKeys.length) return [new Map(acc)];
    return (fieldValues.get(fieldKeys[i]!) ?? [FIELD_OTHER]).flatMap((v) => {
      acc.set(fieldKeys[i]!, v);
      const branch = fieldAssignments(i + 1, acc);
      acc.delete(fieldKeys[i]!);
      return branch;
    });
  };
  const tagStates = tagAssignments(0, new Map());
  const fieldStates = fieldAssignments(0, new Map());

  for (const delivered of bools) {
    for (const closed of bools) {
      for (const active of bools) {
        for (const verdict of verdicts) {
          if (verdict !== 'none' && !delivered) continue; // judgment needs a standing tender
          for (const tags of tagStates) {
            for (const fields of fieldStates) {
              const state: MeansEnumState = { delivered, closed, active, verdict, tags, fields };
              if (test(state)) return state;
            }
          }
        }
      }
    }
  }
  return null;
}

/** An example state in the one pair vocabulary, restricted to the dimensions
 *  the colliding sets mention (everything else is irrelevant to both). */
function describeEnumState(s: MeansEnumState, sets: readonly (readonly MeansPair[])[]): Message {
  const parts: Message[] = [];
  const all = sets.flat();
  const has = (attr: MeansPair['attr']) => all.some((p) => p.attr === attr);
  if (has('delivered') || has('verdict')) {
    parts.push(describeMeansPair({ attr: 'delivered', value: s.delivered }));
  }
  if (has('closed')) parts.push(describeMeansPair({ attr: 'closed', value: s.closed }));
  if (has('active')) {
    parts.push(describeMeansPair({ attr: 'active', value: s.active }));
  }
  if (has('verdict')) parts.push(describeMeansPair({ attr: 'verdict', value: s.verdict }));
  for (const [key, worn] of s.tags) {
    parts.push(describeMeansPair({ attr: 'signal', key, value: worn ? 'worn' : 'clear' }));
  }
  for (const [key, v] of s.fields) {
    parts.push(
      v === FIELD_OTHER
        ? m('attributes.pair.field.other', { field: keyWord(key) })
        : describeMeansPair({ attr: 'field', key, value: v }),
    );
  }
  return joinPairs(parts) ?? verbatim('');
}

/** A set's identity for the duplicate-set refusal (order-free; non-empty sets
 *  only - any number of empty manual words is legal, §11). */
export function meansSetIdentity(means: readonly MeansPair[]): string {
  return means
    .map((p) => `${meansPairIdentity(p)}=${String(p.value)}`)
    .sort()
    .join('|');
}

/**
 * The save-time exclusivity law (§11): every pair of NON-EMPTY `means` sets
 * must be pairwise exclusive over the reachable attribute states -
 * exclusivity is what makes the pull target unique, i.e. deterministic.
 * Returns the first collision (both words + an example state), or null.
 */
export function meansCollision(options: readonly FlowPhaseRow[]): MeansCollision | null {
  const mapped = options.filter((o) => o.means.length > 0);
  for (let i = 0; i < mapped.length; i++) {
    for (let j = i + 1; j < mapped.length; j++) {
      const a = mapped[i]!;
      const b = mapped[j]!;
      const example = findReachableState(
        [a.means, b.means],
        (s) => holdsInEnumState(a.means, s) && holdsInEnumState(b.means, s),
      );
      if (example) {
        return {
          aKey: a.key,
          aLabel: a.label,
          bKey: b.key,
          bLabel: b.label,
          example: describeEnumState(example, [a.means, b.means]),
        };
      }
    }
  }
  return null;
}

/** A non-empty `means` no reachable state satisfies (e.g. a verdict pair on
 *  an undelivered word) - a word that could never hold or be honestly
 *  applied. Returns the offending row, or null. */
export function unreachableMeans(options: readonly FlowPhaseRow[]): FlowPhaseRow | null {
  for (const o of options) {
    if (o.means.length === 0) continue;
    if (findReachableState([o.means], (s) => holdsInEnumState(o.means, s)) === null) return o;
  }
  return null;
}

// --- Validation against the world (catalog + fields as INPUTS; pure) ----------

export interface FlowDefinitionInputs {
  /** the platform's health keys (`HEALTH_STATES`): every declare/clear target and every `tag`
   *  criterion must name one (§3 law 1 - stages apply CATALOG states only). */
  catalogKeys: ReadonlySet<string>;
  /** known field keys per non-flow module namespace (code modules, `ctx:`
   *  contexts) - the "main field" side of set-field/fieldValue references. */
  moduleFields: ReadonlyMap<string, ReadonlySet<string>>;
  /** the definition's concrete `flow:<id>` key, when it already has one;
   *  references through it validate against the definition's own fields
   *  exactly like `FLOW_SELF_MODULE_KEY`. */
  selfModuleKey?: string;
}

/** Where in a definition a reference sits: one stage's block (`whenIn`,
 *  `thenOut`, `means`, ...), named for the refusal. */
function stageBlock(stage: string, block: string): Message {
  return m('flowErrors.where.stageBlock', { stage, block });
}

/** §3 law 3: an entering declaration without a leaving clear of the same key.
 *  LEGAL (asymmetric wiring stays possible) but explicit in the result, so the
 *  editor can warn - the pair is the default, the split is the edit. */
export interface UnpairedDeclare {
  optionKey: string;
  tagKey: string;
}

export interface ValidatedFlowDefinition {
  definition: FlowDefinition;
  unpairedDeclares: UnpairedDeclare[];
}

/**
 * The decide-style whole-definition validation: shape-parse, then check every
 * reference against the provided world (pure - the catalog and foreign fields
 * arrive as inputs, the flow's own fields come from the definition itself).
 * Field-VALUE typing is deliberately not checked here: `decide()`/the module
 * kit re-validate at dispatch (§8b, the staleness guard) - existence is the
 * schema-safety line (§6: no edit may leave an automation citing nothing).
 */
export function validateFlowDefinition(
  value: unknown,
  inputs: FlowDefinitionInputs,
): Result<ValidatedFlowDefinition> {
  const parsed = parseFlowDefinition(value);
  if (!parsed.ok) return parsed;
  const def = parsed.value;
  const selfFields = new Set(def.fields.map((f) => f.key));
  const isSelf = (moduleKey: string) =>
    moduleKey === FLOW_SELF_MODULE_KEY || moduleKey === inputs.selfModuleKey;

  const checkField = (where: Message, moduleKey: string, fieldKey: string): Result<void> => {
    if (isSelf(moduleKey)) {
      return selfFields.has(fieldKey)
        ? ok(undefined)
        : errOf('invalid_input', m('flowErrors.ref.unknownFlowField', { where, field: fieldKey }));
    }
    const known = inputs.moduleFields.get(moduleKey);
    if (!known) {
      return errOf(
        'invalid_input',
        m('flowErrors.ref.unknownModule', { where, module: moduleKey }),
      );
    }
    return known.has(fieldKey)
      ? ok(undefined)
      : errOf(
          'invalid_input',
          m('flowErrors.ref.unknownField', { where, module: moduleKey, field: fieldKey }),
        );
  };
  const checkTag = (where: Message, tagKey: string): Result<void> =>
    inputs.catalogKeys.has(tagKey)
      ? ok(undefined)
      : errOf('invalid_input', m('flowErrors.ref.unknownTag', { where, tag: tagKey }));
  const checkCriteria = (where: Message, criteria: StateCriterion[]): Result<void> => {
    for (const c of criteria) {
      if (c.field === 'signal') {
        const r = checkTag(where, c.value);
        if (!r.ok) return r;
      } else if (c.field === 'fieldValue') {
        const r = checkField(where, c.moduleKey, c.fieldKey);
        if (!r.ok) return r;
      }
    }
    return ok(undefined);
  };

  const unpairedDeclares: UnpairedDeclare[] = [];
  for (const option of def.stages) {
    const where = (block: string) => stageBlock(option.key, block);
    const predicates = [
      ['derivePredicate', option.derivePredicate],
      ['whenIn', option.whenIn],
      ['whenOut', option.whenOut],
    ] as const;
    for (const [block, pred] of predicates) {
      if (!pred) continue;
      const r = checkCriteria(where(block), pred.criteria);
      if (!r.ok) return r;
    }
    const actionBlocks = [
      ['thenIn', option.thenIn],
      ['thenOut', option.thenOut],
    ] as const;
    for (const [block, actions] of actionBlocks) {
      for (const action of actions ?? []) {
        if (action.kind === 'declare' || action.kind === 'clear') {
          const r = checkTag(where(block), action.tagKey);
          if (!r.ok) return r;
        } else if (action.kind === 'set-field') {
          const r = checkField(where(block), action.moduleKey, action.fieldKey);
          if (!r.ok) return r;
        }
      }
    }
    // The v0 gate's requiredFields are flow-field references too (docs/FLOW.md:
    // keys under the flow's own namespace) - the same existence line applies.
    for (const fieldKey of option.gate.requiredFields ?? []) {
      if (!selfFields.has(fieldKey)) {
        return errOf(
          'invalid_input',
          m('flowErrors.stage.requiresUnknownField', { stage: option.key, field: fieldKey }),
        );
      }
    }
    // The means pairs reference the same world: tag pairs name catalog
    // states, field pairs name the flow's OWN fields (§11 - a bare key under
    // the flow's namespace).
    for (const pair of option.means) {
      if (pair.attr === 'signal') {
        const r = checkTag(where('means'), pair.key);
        if (!r.ok) return r;
      } else if (pair.attr === 'field' && !selfFields.has(pair.key)) {
        return errOf(
          'invalid_input',
          m('flowErrors.ref.unknownFlowField', { where: where('means'), field: pair.key }),
        );
      }
    }
    const clears = new Set(
      (option.thenOut ?? []).filter((a) => a.kind === 'clear').map((a) => a.tagKey),
    );
    for (const action of option.thenIn ?? []) {
      if (action.kind === 'declare' && !clears.has(action.tagKey)) {
        unpairedDeclares.push({ optionKey: option.key, tagKey: action.tagKey });
      }
    }
  }
  // A non-empty `means` that no reachable state satisfies is a word that
  // could never hold or be honestly applied - refused, never silent furniture.
  const dead = unreachableMeans(def.stages);
  if (dead) {
    return errOf(
      'invalid_input',
      m('flowErrors.means.unreachable', { stage: dead.label, means: describeMeans(dead.means)! }),
    );
  }
  // Duplicate NON-EMPTY sets are refused with their own message (§11: phases
  // dedupe uncertainty; any number of empty manual words stays legal).
  const byIdentity = new Map<string, FlowPhaseRow>();
  for (const option of def.stages) {
    if (option.means.length === 0) continue;
    const id = meansSetIdentity(option.means);
    const prior = byIdentity.get(id);
    if (prior) {
      return errOf(
        'invalid_input',
        m('flowErrors.means.duplicate', {
          a: prior.label,
          b: option.label,
          means: describeMeans(option.means)!,
        }),
      );
    }
    byIdentity.set(id, option);
  }
  // The exclusivity law (§11): every pair of non-empty sets must be pairwise
  // exclusive over the reachable attribute states - what makes the pull
  // target unique. REFUSED with both words and an example state.
  const collision = meansCollision(def.stages);
  if (collision) {
    return errOf(
      'invalid_input',
      m('flowErrors.means.collision', {
        a: collision.aLabel,
        b: collision.bLabel,
        example: collision.example,
      }),
    );
  }
  // §3 (docs/RESOLUTION.md): every question a dispatch can raise carries a
  // policy once saved - one the author left open is written down as `skip`,
  // visibly, never left implicit. A policy for a question the act cannot
  // reach is refused: it would be furniture nothing reads.
  const stages: FlowOption[] = [];
  for (const option of def.stages) {
    const thenIn = withDispatchPolicies(option.thenIn, stageBlock(option.key, 'thenIn'));
    if (!thenIn.ok) return thenIn;
    const thenOut = withDispatchPolicies(option.thenOut, stageBlock(option.key, 'thenOut'));
    if (!thenOut.ok) return thenOut;
    stages.push({
      ...option,
      ...(thenIn.value ? { thenIn: thenIn.value } : {}),
      ...(thenOut.value ? { thenOut: thenOut.value } : {}),
    });
  }
  return ok({ definition: { ...def, stages }, unpairedDeclares });
}

/**
 * A block's actions with every dispatch's policies NORMALIZED (§3): one entry
 * per question class its act can reach, `skip` where the author named none.
 * Refuses a policy for a class the act cannot reach. Pure.
 */
export function withDispatchPolicies(
  actions: readonly FlowAction[] | undefined,
  where: Message,
): Result<FlowAction[] | undefined> {
  if (!actions) return ok(undefined);
  const out: FlowAction[] = [];
  for (const action of actions) {
    if (action.kind !== 'dispatch') {
      out.push(action);
      continue;
    }
    const reachable = dispatchQuestionClasses(action.command);
    for (const klass of Object.keys(action.policies ?? {})) {
      if (!reachable.includes(klass as QuestionClass)) {
        return errOf(
          'invalid_input',
          m('flowErrors.policy.unreachable', {
            where,
            command: action.command.type,
            question: klass,
          }),
        );
      }
    }
    const policies: DispatchPolicies = {};
    for (const klass of reachable) policies[klass] = action.policies?.[klass] ?? 'skip';
    out.push({ ...action, policies });
  }
  return ok(out);
}

// --- Retirement (§6: removals surface their references) ------------------------

export type FlowReferenceBlock =
  | 'derivePredicate'
  | 'whenIn'
  | 'whenOut'
  | 'thenIn'
  | 'thenOut'
  | 'means'
  | 'gate.requiredFields';

export interface FlowFieldReference {
  optionKey: string;
  block: FlowReferenceBlock;
}

/** Every place a definition cites one of its own flow-scoped field keys
 *  (through `flow:self` / the concrete self key, plus the v0 gate's
 *  requiredFields). The editor's "this removal breaks these" surface. */
export function referencesToFlowField(
  def: FlowDefinition,
  fieldKey: string,
  selfModuleKey?: string,
): FlowFieldReference[] {
  const isSelf = (moduleKey: string) =>
    moduleKey === FLOW_SELF_MODULE_KEY || moduleKey === selfModuleKey;
  const out: FlowFieldReference[] = [];
  for (const option of def.stages) {
    const predicates = [
      ['derivePredicate', option.derivePredicate],
      ['whenIn', option.whenIn],
      ['whenOut', option.whenOut],
    ] as const;
    for (const [block, pred] of predicates) {
      if (
        pred?.criteria.some(
          (c) => c.field === 'fieldValue' && isSelf(c.moduleKey) && c.fieldKey === fieldKey,
        )
      ) {
        out.push({ optionKey: option.key, block });
      }
    }
    // A means field pair names the flow's OWN field by bare key (§11).
    if (option.means.some((p) => p.attr === 'field' && p.key === fieldKey)) {
      out.push({ optionKey: option.key, block: 'means' });
    }
    const actionBlocks = [
      ['thenIn', option.thenIn],
      ['thenOut', option.thenOut],
    ] as const;
    for (const [block, actions] of actionBlocks) {
      if (
        (actions ?? []).some(
          (a) => a.kind === 'set-field' && isSelf(a.moduleKey) && a.fieldKey === fieldKey,
        )
      ) {
        out.push({ optionKey: option.key, block });
      }
    }
    if ((option.gate.requiredFields ?? []).includes(fieldKey)) {
      out.push({ optionKey: option.key, block: 'gate.requiredFields' });
    }
  }
  return out;
}

export interface FlowDefinitionRemovals {
  /** options removed by the edit. Definition-internal citations of an option
   *  key do not exist; the LIVE references (members' stored positions) are
   *  server data the effectful layer attaches before accepting the edit. */
  removedOptions: { key: string; label: string }[];
  /** fields removed by the edit, each with the citations REMAINING in `next`
   *  (a gate/predicate/action still naming it) - returned so the editor
   *  re-wires or retires them in the same gesture; `validateFlowDefinition`
   *  is the backstop that refuses the dangling state outright. */
  removedFields: { key: string; label: string; references: FlowFieldReference[] }[];
}

/** Diff two definition versions and surface what the removals break (§6).
 *  Values are NEVER destroyed by any of this - retirement rendering is the
 *  read layer's law; this is the authoring-time surface. */
export function flowDefinitionRemovals(
  prev: FlowDefinition,
  next: FlowDefinition,
  selfModuleKey?: string,
): FlowDefinitionRemovals {
  const nextOptionKeys = new Set(next.stages.map((o) => o.key));
  const nextFieldKeys = new Set(next.fields.map((f) => f.key));
  return {
    removedOptions: prev.stages
      .filter((o) => !nextOptionKeys.has(o.key))
      .map((o) => ({ key: o.key, label: o.label })),
    removedFields: prev.fields
      .filter((f) => !nextFieldKeys.has(f.key))
      .map((f) => ({
        key: f.key,
        label: f.label,
        references: referencesToFlowField(next, f.key, selfModuleKey),
      })),
  };
}

// --- Self-key resolution (dispatch-time) ---------------------------------------

/** Rewrite every `flow:self` module reference to the definition's concrete
 *  `flow:<flowId>` key - the pure step between the copy-safe stored payload
 *  and evaluation/dispatch against a real item's `module_data`. */
export function resolveFlowSelfKeys(def: FlowDefinition, flowId: string): FlowDefinition {
  const concrete = flowModuleKey(flowId);
  const resolvePredicate = (pred: CriteriaPredicate): CriteriaPredicate => ({
    op: pred.op,
    criteria: pred.criteria.map((c) =>
      c.field === 'fieldValue' && c.moduleKey === FLOW_SELF_MODULE_KEY
        ? { ...c, moduleKey: concrete }
        : c,
    ),
  });
  const resolveActions = (actions: FlowAction[]): FlowAction[] =>
    actions.map((a) =>
      a.kind === 'set-field' && a.moduleKey === FLOW_SELF_MODULE_KEY
        ? { ...a, moduleKey: concrete }
        : a,
    );
  return {
    ...def,
    stages: def.stages.map((option): FlowOption => {
      const wiring: FlowOptionWiring = {
        ...(option.who ? { who: option.who } : {}),
        ...(option.whenIn ? { whenIn: resolvePredicate(option.whenIn) } : {}),
        ...(option.whenOut ? { whenOut: resolvePredicate(option.whenOut) } : {}),
        ...(option.thenIn ? { thenIn: resolveActions(option.thenIn) } : {}),
        ...(option.thenOut ? { thenOut: resolveActions(option.thenOut) } : {}),
      };
      const base = {
        key: option.key,
        label: option.label,
        ...(option.description ? { description: option.description } : {}),
        // Means field pairs name the flow's own field by BARE key (never a
        // module path), so they need no self-key rewrite.
        means: option.means,
        gate: option.gate,
      };
      return option.kind === 'derived'
        ? {
            ...base,
            ...wiring,
            kind: 'derived',
            derivePredicate: resolvePredicate(option.derivePredicate),
          }
        : { ...base, ...wiring, kind: 'stored' };
    }),
  };
}

// --- Lane placement (§4: the select collapse; the read layer's fold) -----------

/** One flow member's placement facts: the LOGGED position plus the vector
 *  snapshot the derive-predicates evaluate over. */
export interface FlowMemberPlacementFacts {
  itemId: string;
  /** the member's logged position key (`flow_memberships.flowPhaseKey`). */
  flowPhaseKey: string;
  snapshot: VectorSnapshot;
}

export interface FlowLanePlacement {
  /** option key -> member item ids, every option present (possibly empty),
   *  members in input order. A member sits in AT MOST ONE lane - a select is
   *  one dimension. */
  byOption: Map<string, string[]>;
  /** members no lane claims: the logged key is gone from the definition (or
   *  names a derived option) and no derive-predicate holds. Drift, surfaced -
   *  never auto-corrected, never silently dropped. */
  unplaced: string[];
}

/**
 * Where each member renders, per FLOW_DEFINITION §4 - THE lane-membership rule
 * (v1, S4).
 *
 * **Scope (v1, deliberate): derived membership evaluates ONLY over items that
 * JOINED the flow.** Joining stays the explicit act; a derive-predicate decides
 * WHICH lane a member sits in, never WHO is a member - otherwise every derived
 * lane would be an org-wide standing query, and flow boards would collect items
 * nobody placed there.
 *
 * Resolution order per member (each member lands in at most one lane):
 *   1. If the logged position names a STORED option, that lane claims it - the
 *      stored choice is a logged claim and it STANDS; when its meaning stops
 *      holding that is drift, visible information, never auto-corrected (§4).
 *      A stored placement is therefore never overridden by a matching
 *      derive-predicate elsewhere (the mixed-flow case: an item deliberately
 *      parked in "Polish" stays there while its seat is still held).
 *   2. Otherwise (the logged key names a derived option, or a key no longer in
 *      the definition): the FIRST derived option in definition order whose
 *      predicate the snapshot satisfies claims it - membership is COMPUTED, the
 *      card can never disagree with the machine, and a vector change relocates
 *      the card with zero automation. Overlapping derived predicates resolve by
 *      definition order (first wins), deterministically.
 *   3. Otherwise unplaced (drift, surfaced).
 */
export function placeFlowMembers(
  options: FlowOption[],
  members: FlowMemberPlacementFacts[],
): FlowLanePlacement {
  const byOption = new Map<string, string[]>(options.map((o) => [o.key, []]));
  const storedKeys = new Set(options.filter((o) => o.kind === 'stored').map((o) => o.key));
  const derived = options.filter((o) => o.kind === 'derived');
  const unplaced: string[] = [];
  for (const m of members) {
    if (storedKeys.has(m.flowPhaseKey)) {
      byOption.get(m.flowPhaseKey)!.push(m.itemId);
      continue;
    }
    const lane = derived.find((o) => evaluatePredicate(m.snapshot, o.derivePredicate));
    if (lane) byOption.get(lane.key)!.push(m.itemId);
    else unplaced.push(m.itemId);
  }
  return { byOption, unplaced };
}

// --- Plain-words criteria (§5: refusals state their reason at the bounce) ------

/** One criterion as a human phrase - the vocabulary of gate refusals
 *  ("needs: delivered") and the editor's prose rendering (§5), as a descriptor
 *  the surface formats (docs/I18N.md §6). A values list joins by "or"; a
 *  negated criterion says "not" after its attribute word, as the filter bar's
 *  chip does (docs/SEARCH.md "Criteria operators"). */
export function describeCriterion(c: StateCriterion): Message {
  const not = isNegated(c);
  switch (c.field) {
    case 'tendered':
      return m(c.value ? 'flowErrors.criterion.delivered' : 'flowErrors.criterion.notDelivered');
    case 'judgment': {
      // The judgment's three words are a closed set, so a negated list reads
      // as the words it leaves (never "not no verdict yet").
      const left = not ? JUDGMENTS.filter((j) => !c.values.includes(j)) : c.values;
      if (left.length > 0) return orList(left.map((j) => JUDGMENT_WORD[j]));
      // All three negated: no item can hold it, and the phrase says so.
      return m('flowErrors.criterion.not', {
        criterion: orList(c.values.map((j) => JUDGMENT_WORD[j])),
      });
    }
    case 'cancelled':
      return m(c.value ? 'flowErrors.criterion.cancelled' : 'flowErrors.criterion.notCancelled');
    case 'done':
      return m(c.value ? 'flowErrors.criterion.done' : 'flowErrors.criterion.notDone');
    case 'seat': {
      const requester = c.role === 'requester';
      if (c.empty) {
        // Negated, the empty seat is a held one.
        return requester
          ? m(
              not
                ? 'flowErrors.criterion.requesterSeated'
                : 'flowErrors.criterion.noRequesterSeated',
            )
          : m(
              not
                ? 'flowErrors.criterion.contributorSeated'
                : 'flowErrors.criterion.noContributorSeated',
            );
      }
      const ref = orList(c.values);
      if (requester) {
        return not
          ? m('flowErrors.criterion.requesterIsNot', { ref })
          : m('flowErrors.criterion.requesterIs', { ref });
      }
      return not
        ? m('flowErrors.criterion.contributorIsNot', { ref })
        : m('flowErrors.criterion.contributorIs', { ref });
    }
    case 'signal':
      return m('flowErrors.criterion.wearing', { signal: c.value });
    case 'tag': {
      if (c.empty) return m(not ? 'flowErrors.criterion.hasTag' : 'flowErrors.criterion.untagged');
      const tags = orList(c.values.map((tag) => m('flowErrors.criterion.tagValue', { tag })));
      return not
        ? m('flowErrors.criterion.notTagged', { tags })
        : m('flowErrors.criterion.tagged', { tags });
    }
    case 'fieldValue':
      return c.value === null || c.value === ''
        ? m('flowErrors.criterion.fieldEmpty', { field: c.fieldKey })
        : m('flowErrors.criterion.fieldIs', { field: c.fieldKey, value: String(c.value) });
    case 'closed':
      return m(c.value ? 'flowErrors.criterion.closed' : 'flowErrors.criterion.notClosed');
    case 'active':
      return m(c.value ? 'flowErrors.criterion.active' : 'flowErrors.criterion.notActive');
    case 'level':
      return not
        ? m('flowErrors.criterion.levelBelow', { level: LEVEL_WORD[c.value] })
        : m('flowErrors.criterion.levelAtLeast', { level: LEVEL_WORD[c.value] });
    case 'assignee': {
      if (c.empty) {
        return m(not ? 'flowErrors.criterion.contributorSeated' : 'flowErrors.criterion.unclaimed');
      }
      const ref = orList(c.values);
      return not
        ? m('flowErrors.criterion.notAssignedTo', { ref })
        : m('flowErrors.criterion.assignedTo', { ref });
    }
    case 'party': {
      if (c.empty) {
        return m(not ? 'flowErrors.criterion.hasParties' : 'flowErrors.criterion.noParties');
      }
      const ref = orList(c.values);
      return not
        ? m('flowErrors.criterion.partiesExclude', { ref })
        : m('flowErrors.criterion.partiesInclude', { ref });
    }
    case 'group': {
      if (c.empty) return m(not ? 'flowErrors.criterion.inAGroup' : 'flowErrors.criterion.noGroup');
      const group = orList(c.values);
      return not
        ? m('flowErrors.criterion.notInGroup', { group })
        : m('flowErrors.criterion.inGroup', { group });
    }
    case 'parent': {
      if (c.empty) {
        return m(not ? 'flowErrors.criterion.hasParent' : 'flowErrors.criterion.noParent');
      }
      const parent = orList(c.values);
      return not
        ? m('flowErrors.criterion.notChildOf', { parent })
        : m('flowErrors.criterion.childOf', { parent });
    }
    case 'ancestor': {
      const parent = orList(c.values);
      return not
        ? m('flowErrors.criterion.notUnder', { parent })
        : m('flowErrors.criterion.under', { parent });
    }
    case 'title':
      return not
        ? m('flowErrors.criterion.titleLacks', { text: c.value })
        : m('flowErrors.criterion.titleContains', { text: c.value });
  }
}

/** The judgment words a criterion names. */
const JUDGMENT_WORD: Record<Judgment, Message> = {
  none: m('flowErrors.criterion.noVerdict'),
  accepted: m('flowErrors.criterion.accepted'),
  disputed: m('flowErrors.criterion.disputed'),
};

/** Values joined by "or" ("a or b or c"), as one descriptor (a lone value
 *  stays as it is). A criterion's values list is never empty. */
function orList(values: readonly Message[]): Message;
function orList(values: readonly (string | Message)[]): Message | string;
function orList(values: readonly (string | Message)[]): Message | string {
  if (values.length === 0) return '';
  return values
    .slice(0, -1)
    .reduceRight<
      Message | string
    >((second, first) => m('flowErrors.criterion.or', { first, second }), values[values.length - 1]!);
}

/** A health level as a criterion names it ("health at least warning"). */
const LEVEL_WORD: Record<Level, Message> = {
  info: m('flowErrors.level.info'),
  notice: m('flowErrors.level.notice'),
  warning: m('flowErrors.level.warning'),
  critical: m('flowErrors.level.critical'),
};

/** The criteria a snapshot fails a predicate on: [] when the predicate is
 *  satisfied; under `all` the failing criteria; under `any` (none matched) the
 *  whole list - the person needs to make ONE of them true. */
export function unmetCriteria(snap: VectorSnapshot, pred: CriteriaPredicate): StateCriterion[] {
  if (pred.criteria.length === 0) return [];
  if (pred.op === 'any') {
    return pred.criteria.some((c) => matchesStateCriterion(snap, c)) ? [] : [...pred.criteria];
  }
  return pred.criteria.filter((c) => !matchesStateCriterion(snap, c));
}

/** The unmet criteria side by side ("delivered, a contributor seated"), and
 *  whether the person needs only ONE of them. */
function unmetList(
  op: 'all' | 'any',
  unmet: StateCriterion[],
): { criteria: Message; oneOf: boolean } {
  const criteria = unmet
    .map(describeCriterion)
    .reduceRight((second, first) => m('flowErrors.criterion.joined', { first, second }));
  return { criteria, oneOf: op === 'any' && unmet.length > 1 };
}

// --- The move decision (§1 Who/When; ruling §7.5: disabled with its reason) ----

/** The authority facts a Who selects over, resolved by the caller against the
 *  EXISTING primitives (§1: the item's seats, the viewer's role ON the flow,
 *  plain write standing) - never a new permission store. */
export interface StageMoveWhoFacts {
  /** the viewer holds a requester seat of the item. */
  isRequester: boolean;
  /** the viewer holds a contributor seat of the item. */
  isContributor: boolean;
  /** the viewer's role ON the flow (`flowRoleFromFacts`, domain/access.ts:
   *  anchor reach ⇒ owner, plus the flow's own grants - so an owner-role
   *  co-owner satisfies an owner Who, and an item-anchored flow's endeavor
   *  owner does too). Null: no reach. Owner satisfies a member requirement.
   *  (The stored selector kind stays `group-role` - a payload shape, not a
   *  payload migration.) */
  flowRole: Role | null;
  /** write standing on the item (the same condition the write gate applies). */
  hasWrite: boolean;
}

/** A Who as a human phrase, for the refusal reason. */
export function describeWho(who: WhoSelector): Message {
  switch (who.kind) {
    case 'anyone-with-write':
      return m('flowErrors.who.anyone');
    case 'seat':
      return m(
        who.role === 'requester' ? 'flowErrors.who.requester' : 'flowErrors.who.contributor',
      );
    case 'group-role':
      return m(who.role === 'owner' ? 'flowErrors.who.owner' : 'flowErrors.who.member');
  }
}

/** Does the viewer satisfy a Who selector? An absent Who is the platform
 *  posture (`DEFAULT_WHO`: anyone with write). */
export function whoSatisfied(who: WhoSelector | undefined, facts: StageMoveWhoFacts): boolean {
  const w = who ?? DEFAULT_WHO;
  switch (w.kind) {
    case 'anyone-with-write':
      return facts.hasWrite;
    case 'seat':
      return w.role === 'requester' ? facts.isRequester : facts.isContributor;
    case 'group-role':
      return facts.flowRole !== null && roleRank(facts.flowRole) >= roleRank(w.role);
  }
}

/** The UI's disabled-with-reason shape (ruling §7.5). The reason is plain
 *  words (a descriptor the surface formats), present exactly when the move is
 *  not allowed. */
export interface StageMoveAbility {
  allowed: boolean;
  reason?: Message;
}

/**
 * The pure move decision the read layer exposes and the UI renders as
 * disabled-with-reason (ruling §7.5): Who on both ends (§1 - a Who guards
 * moves in AND out), then When on both boundaries (the source's `whenOut`,
 * the target's `whenIn`), each unmet criterion named in plain words - stated,
 * never silent. First failure wins (authority before state). This is a READ
 * verdict: the server re-checks at dispatch (S6); nothing here authorizes.
 */
export function decideStageMove(input: {
  /** the option the member currently renders in (null: unplaced / unknown). */
  from: FlowOption | null;
  to: FlowOption;
  snapshot: VectorSnapshot;
  who: StageMoveWhoFacts;
}): StageMoveAbility {
  const { from, to, snapshot, who } = input;
  if (!whoSatisfied(to.who, who)) {
    return {
      allowed: false,
      reason: m('flowErrors.move.whoInto', {
        who: describeWho(to.who ?? DEFAULT_WHO),
        stage: to.label,
      }),
    };
  }
  if (from && from.key !== to.key && !whoSatisfied(from.who, who)) {
    return {
      allowed: false,
      reason: m('flowErrors.move.whoOutOf', {
        who: describeWho(from.who ?? DEFAULT_WHO),
        stage: from.label,
      }),
    };
  }
  if (from && from.key !== to.key && from.whenOut) {
    const unmet = unmetCriteria(snapshot, from.whenOut);
    if (unmet.length > 0) {
      const { criteria, oneOf } = unmetList(from.whenOut.op, unmet);
      return {
        allowed: false,
        reason: oneOf
          ? m('flowErrors.move.leavingOneOf', { stage: from.label, criteria })
          : m('flowErrors.move.leaving', { stage: from.label, criteria }),
      };
    }
  }
  if (to.whenIn) {
    const unmet = unmetCriteria(snapshot, to.whenIn);
    if (unmet.length > 0) {
      const { criteria, oneOf } = unmetList(to.whenIn.op, unmet);
      return {
        allowed: false,
        reason: oneOf
          ? m('flowErrors.move.enteringOneOf', { stage: to.label, criteria })
          : m('flowErrors.move.entering', { stage: to.label, criteria }),
      };
    }
  }
  return { allowed: true };
}
